import { useState, type ReactElement } from 'react';

import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { ModalShell } from '@/components/ui/ModalShell';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';

import { requestError, resolvedToast } from '../presentation';
import type { DocumentRequest } from '../types';
import { useApproveRequest, useCancelRequest, useDocumentRequest } from '../use-document-requests';
import { NewApprovalModal } from './NewApprovalModal';
import { RejectModal } from './RejectModal';
import { RequestSummary } from './RequestSummary';
import { errorAdvice } from '@/lib/error-advice';

// Detalle de una solicitud de documento (G3-1, D11). Calidad: aprobar (una nueva con el
// formulario del alta precargado; una modificación inicia su flujo) o rechazar con
// motivo. Solicitante: solo lectura y "Cancelar solicitud" mientras está pendiente.

const TITLE = 'Solicitud de documento';
type Mode = 'view' | 'reject' | 'cancel';

export function RequestDetailModal({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}): ReactElement {
  const query = useDocumentRequest(id);
  if (query.data === undefined) {
    const gone = query.error instanceof ApiError && query.error.status === 404;
    return (
      <ModalShell
        open
        title={TITLE}
        wide
        onClose={onClose}
        footer={
          <button type="button" className="btn mut sm" onClick={onClose}>
            Cerrar
          </button>
        }
      >
        {query.isError ? (
          <div className="modal-body" role="alert">
            <p className="fld-note">
              {gone
                ? 'La solicitud ya no está disponible. Recarga la bandeja.'
                : `No se pudo cargar la solicitud. ${errorAdvice(query.error)}`}
            </p>
            {!gone && (
              <button type="button" className="btn mut sm" onClick={() => void query.refetch()}>
                Reintentar
              </button>
            )}
          </div>
        ) : (
          <div className="modal-body tsk" aria-busy="true">
            <span className="sr-only">Cargando</span>
            <span className="skeleton-line" aria-hidden="true" />
            <span className="skeleton-line" aria-hidden="true" />
          </div>
        )}
      </ModalShell>
    );
  }
  return <RequestDetail request={query.data} onClose={onClose} />;
}

function RequestDetail({
  request,
  onClose,
}: {
  request: DocumentRequest;
  onClose: () => void;
}): ReactElement {
  const { showToast } = useToast();
  const approve = useApproveRequest(request.id);
  const cancel = useCancelRequest(request.id);
  const [mode, setMode] = useState<Mode>('view');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const done = (resolved: DocumentRequest): void => {
    showToast(resolvedToast(resolved));
    onClose();
  };

  async function run(action: () => Promise<DocumentRequest>): Promise<void> {
    if (busy) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      done(await action());
    } catch (err) {
      setError(requestError(err));
      setMode('view');
    } finally {
      setBusy(false);
    }
  }

  if (mode === 'reject') {
    return <RejectModal request={request} onBack={() => setMode('view')} onDone={done} />;
  }
  if (mode === 'cancel') {
    return (
      <ConfirmModal
        open
        title="Cancelar solicitud"
        destructive
        busy={busy}
        confirmLabel="Sí, cancelar"
        cancelLabel="Volver"
        message={`«${request.proposedTitle}» dejará de estar pendiente y Calidad ya no la verá como tarea.`}
        onCancel={() => setMode('view')}
        onConfirm={() => void run(() => cancel.mutateAsync())}
      />
    );
  }
  if (request.canResolve && request.kind === 'nuevo') {
    return (
      <NewApprovalModal
        request={request}
        onClose={onClose}
        onReject={() => setMode('reject')}
        onDone={done}
      />
    );
  }

  return (
    <ModalShell
      open
      title={TITLE}
      wide
      busy={busy}
      onClose={onClose}
      footer={
        <>
          {request.canResolve && (
            <button
              type="button"
              className="btn danger sm"
              disabled={busy}
              onClick={() => setMode('reject')}
            >
              Rechazar
            </button>
          )}
          {request.canCancel && (
            <button
              type="button"
              className="btn danger sm"
              disabled={busy}
              onClick={() => setMode('cancel')}
            >
              Cancelar solicitud
            </button>
          )}
          <button type="button" className="btn mut sm" onClick={onClose} disabled={busy}>
            Cerrar
          </button>
          {request.canResolve && (
            <button
              type="button"
              className="btn sm"
              disabled={busy}
              onClick={() => void run(() => approve.mutateAsync({}))}
            >
              {busy ? 'Aprobando…' : 'Aprobar e iniciar modificación'}
            </button>
          )}
        </>
      }
    >
      <div className="form-grid">
        <RequestSummary request={request} showProposal />
        {error !== null && (
          <p className="fld-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </ModalShell>
  );
}
